/** Manager side: pick rep + date, see the recorded route, visits and any tracking gaps on a Google map. */
import React, { useEffect, useRef, useState } from 'react';
import { loadGoogleMaps } from '../../lib/googleMaps';
import { FIELD_EVENT } from './FieldTracker';
import { AlertTriangle } from 'lucide-react';
import api from '../../lib/api';
import { Input } from '../ui/input';

const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;
const today = () => new Date().toLocaleDateString('en-CA');

function RouteMap({ data }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!MAPS_KEY || !ref.current || !data?.points?.length) return;
    loadGoogleMaps().then((google) => {
      const path = data.points.map((p) => ({ lat: p.lat, lng: p.lng }));
      const map = new google.maps.Map(ref.current, { center: path[path.length - 1], zoom: 13, streetViewControl: false });
      new google.maps.Polyline({ path, map, strokeColor: '#1a73e8', strokeWeight: 4 });
      data.visits.forEach((v) => new google.maps.Marker({ map, position: { lat: v.lat, lng: v.lng }, title: `${v.merchant_name} — ${v.outcome || 'in progress'}` }));
      const b = new google.maps.LatLngBounds(); path.forEach((p) => b.extend(p)); map.fitBounds(b);
    }).catch(() => {});
  }, [data]);
  if (!data?.points?.length) return <p className="py-12 text-center text-sm text-muted-foreground" data-testid="field-route-empty">No locations recorded for this day.</p>;
  return <div ref={ref} className="h-[420px] w-full rounded-xl border" data-testid="field-route-map" />;
}

export function ManagerPanel() {
  const [reps, setReps] = useState([]);
  const [uid, setUid] = useState('');
  const [date, setDate] = useState(today());
  const [data, setData] = useState(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const bump = () => setTick((t) => t + 1);
    window.addEventListener(FIELD_EVENT, bump); window.addEventListener('field-ping', bump);
    return () => { window.removeEventListener(FIELD_EVENT, bump); window.removeEventListener('field-ping', bump); };
  }, []);
  useEffect(() => { api.request('/field/team').then((d) => { setReps(d.reps); setUid((u) => u || d.reps[0]?.user_id || ''); }).catch(() => {}); }, [tick]);
  useEffect(() => {
    if (!uid) return;
    const q = new URLSearchParams({ user_id: uid, date, tz_offset_min: String(new Date(`${date}T00:00:00`).getTimezoneOffset()) });
    api.request(`/field/route?${q}`).then(setData).catch(() => setData(null));
  }, [uid, date, tick]);
  return (
    <div className="space-y-4 rounded-2xl border bg-card p-5" data-testid="field-manager-panel">
      <div className="flex flex-wrap items-center gap-2">
        <select className="h-9 rounded-md border bg-background px-2 text-sm" value={uid} onChange={(e) => setUid(e.target.value)} data-testid="field-rep-select">
          {!reps.length && <option value="">No reps have shared a location yet</option>}
          {reps.map((r) => <option key={r.user_id} value={r.user_id}>{r.name}{r.on_shift ? ' · on shift' : ''}</option>)}</select>
        <Input type="date" className="h-9 w-44" value={date} onChange={(e) => setDate(e.target.value)} data-testid="field-date" />
        {reps.find((r) => r.user_id === uid)?.last_ping && <span className="text-xs text-muted-foreground" data-testid="field-rep-last">Last seen {new Date(reps.find((r) => r.user_id === uid).last_ping.at).toLocaleString()}</span>}
      </div>
      {data?.gaps?.length > 0 && <p className="flex items-center gap-2 rounded-md bg-amber-50 p-3 text-sm text-amber-800" data-testid="field-gaps"><AlertTriangle className="h-4 w-4" />Route incomplete — {data.gaps.length} gap{data.gaps.length > 1 ? 's' : ''} with no location (CRM closed or phone locked): {data.gaps.map((g) => `${new Date(g.from).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}–${new Date(g.to).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`).join(', ')}</p>}
      <RouteMap data={data} />
      {data?.visits?.length > 0 && <div className="divide-y rounded-lg border text-sm" data-testid="field-visits">{data.visits.map((v) => (
        <div key={v.visit_id} className="flex flex-wrap justify-between gap-2 p-3"><span><b>{v.merchant_name}</b> · {v.outcome || 'in progress'}{v.notes ? ` — ${v.notes}` : ''}</span>
          <span className="text-muted-foreground">{new Date(v.checked_in_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}{v.checked_out_at ? `–${new Date(v.checked_out_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}` : ''}</span></div>))}</div>}
    </div>
  );
}
